import { PresetTag, SoftTag } from '../../Tag/SoftTag';
import {
  ChatDrawer,
  CustomerDetailsCard,
  JobDetailsCard,
  OrderHeaderCards,
  OrderInvoiceCard,
  OrderPresenceAlert,
  OrderServiceSelect,
  OrderToolbar,
  RegPlate,
} from '@components';
import { useAccess, useView } from '@hooks';
import {
  LocalizedServiceOrderStatus,
  LocalizedServiceOrderStatusObjectKey,
  isEstimateOut,
  isOrderLocked,
  OrderStatus,
  ServiceOrderStatusColor,
  ServiceOrderStatusColorObjectKey,
} from '@models';
import { useStore } from '@stores';
import { observer } from 'mobx-react';
import { useState } from 'react';

export const OrderView = observer(() => {
  const { orderStore } = useStore();
  const { order: access } = useAccess();
  const { order } = orderStore;
  const { isMobile } = useView();

  const [chatOpen, setChatOpen] = useState(false);
  const [workPickerOpen, setWorkPickerOpen] = useState(false);
  // Print is an order-wide action like Add work, so the toolbar holds them side by side.
  const [printsSlot, setPrintsSlot] = useState<HTMLSpanElement | null>(null);

  // Same rule OrderServiceSelect applies to its own select: no edits with an estimate out or once invoiced.
  const servicesLocked = !!order && (isEstimateOut(order) || isOrderLocked(order.status));

  if (!order) return null;

  const jobCard = <JobDetailsCard />;

  // The order already names its vehicle; the full record (and a VIN the BE may not have given
  // us) is only fetched when someone opens the vehicle details.
  const customerCard = (
    <CustomerDetailsCard
      compact
      customer={order.customer}
      vehicleSummary={{
        registrationNumber: order.registrationNumber,
        makeAndModel: order.vehicleModel,
        vin: order.vin,
      }}
      onOpenChat={() => setChatOpen(true)}
      currentMileage={order.mileage}
    />
  );

  const invoiceCardContent = (
    <OrderInvoiceCard order={order} iconOnly />
  );

  // Collapsed-panel facts for the phone layout (the desktop cards show them in full).
  const statusLabel =
    LocalizedServiceOrderStatus[order.status as LocalizedServiceOrderStatusObjectKey] || order.status;
  const statusColor = ServiceOrderStatusColor[order.status as ServiceOrderStatusColorObjectKey];
  const jobSummary = (
    <>
      <PresetTag color={statusColor} style={{ margin: 0, color: 'rgba(0,0,0,0.75)' }}>
        {statusLabel}
      </PresetTag>
      <span>{order.user?.fullname ?? 'Unassigned'}</span>
    </>
  );
  const customerSummary = (
    <>
      <span>{order.customer?.fullname}</span>
      {order.registrationNumber && <RegPlate size="small">{order.registrationNumber}</RegPlate>}
      {!order.vin && (
        <SoftTag group="yellow" style={{ margin: 0 }}>
          No VIN
        </SoftTag>
      )}
    </>
  );
  // The invoice itself is only fetched once the panel opens, so the state comes from the order:
  // Ready = invoiced and awaiting payment; Completed = the job is done and can be invoiced.
  const invoiceSummary =
    order.status === OrderStatus.Paid
      ? 'Paid'
      : order.status === OrderStatus.Ready
        ? 'Invoiced - awaiting payment'
        : order.status === OrderStatus.Completed
          ? 'Ready to invoice'
          : 'Not invoiced';

  return (
    <>
      <OrderPresenceAlert orderId={order?.id} />

      {access.headerCards && (
        <OrderHeaderCards
          expanded={orderStore.orderDetailsExpanded}
          jobCard={jobCard}
          customerCard={customerCard}
          invoiceCard={access.invoiceCard ? invoiceCardContent : undefined}
          jobSummary={jobSummary}
          customerSummary={customerSummary}
          invoiceSummary={invoiceSummary}
        />
      )}

      {/* The job itself - services, labour and parts - is the page; everything else is a drawer
          opened from the row at the top of it. */}
      <div
        className={isMobile ? 'order-tabs-mobile' : undefined}
        style={{
          background: '#fff',
          borderRadius: 10,
          padding: isMobile ? '8px 16px 16px' : '12px 16px 16px',
          marginTop: isMobile ? 0 : 12,
        }}
      >
        <OrderToolbar
          order={order}
          onAddWork={servicesLocked ? undefined : () => setWorkPickerOpen(true)}
          addWorkLockedReason={
            servicesLocked
              ? isEstimateOut(order)
                ? 'Cannot add work while the estimate is out - revise it instead'
                : 'This job is invoiced, so its work cannot be changed'
              : undefined
          }
          printsRef={setPrintsSlot}
        />
        {/* Totals are in the drawer footer and the invoice card, so no strip here; services are
            added from the toolbar's "Add work", which opens the picker this component owns. */}
        <OrderServiceSelect
          atLeastOneService
          hideHeader
          pickerOpen={workPickerOpen}
          actionsSlot={printsSlot}
          onPickerClose={() => setWorkPickerOpen(false)}
        />
      </div>

      {order.customer?.userId && (
        <ChatDrawer
          open={chatOpen}
          channelId={order.requestId ?? order.id}
          customerUserId={order.customer.userId}
          title={`Chat with ${order.customer.fullname}`}
          onClose={() => setChatOpen(false)}
        />
      )}
    </>
  );
});
